import { SectionHeader } from "../shared/SectionHeader";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useTheme, type Theme } from "@/contexts/ThemeContext";
import { Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";

const ACCENT_COLORS = ["#16a34a", "#2563eb", "#9333ea", "#dc2626", "#d97706", "#0d9488"];

const THEME_OPTIONS: { value: Theme; label: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", icon: <Sun className="h-4 w-4" /> },
  { value: "dark", label: "Dark", icon: <Moon className="h-4 w-4" /> },
  { value: "system", label: "System", icon: <Monitor className="h-4 w-4" /> },
];

const DEFAULT_ACCENT = "#16a34a";

export function AppAppearance() {
  const { theme, setTheme, accentColor, setAccentColor } = useTheme();
  const effectiveAccent = accentColor ?? DEFAULT_ACCENT;

  return (
    <>
      <SectionHeader
        title="App Appearance"
        description="Theme and display preferences (saved per user)."
      />
      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Theme</h3>
          <p className="text-[12px] text-muted-foreground">
            Choose light, dark, or match your device (system). Changes apply immediately.
          </p>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={theme}
            onValueChange={(v) => setTheme(v as Theme)}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6"
          >
            {THEME_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={cn(
                  "flex items-center space-x-3 rounded-lg border p-4 transition-colors",
                  theme === opt.value ? "border-primary bg-primary/5" : "border-border"
                )}
              >
                <RadioGroupItem value={opt.value} id={`theme-${opt.value}`} />
                <Label
                  htmlFor={`theme-${opt.value}`}
                  className="flex items-center gap-2 cursor-pointer text-sm font-medium text-foreground"
                >
                  {opt.icon}
                  {opt.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Accent Color</h3>
          <p className="text-[12px] text-muted-foreground">
            Choose a colour for buttons, links, and highlights. Applies across the app and sidebar.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap items-center">
            {ACCENT_COLORS.map((hex) => (
              <button
                key={hex}
                type="button"
                className={cn(
                  "w-8 h-8 rounded-full border-2 transition-colors hover:opacity-90",
                  effectiveAccent === hex ? "border-foreground ring-2 ring-primary ring-offset-2 ring-offset-background" : "border-border hover:border-primary/50"
                )}
                style={{ backgroundColor: hex }}
                title={hex}
                onClick={() => setAccentColor(hex)}
              />
            ))}
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => setAccentColor(null)}
            >
              Reset to default
            </button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
